import transcendCssUrl from "./transcend-consent.css?url";

/**
 * Transcend Consent Management (airgap.js)
 *
 * Loads Transcend's `airgap.js` — the Privacy & Cookie Consent platform LG uses
 * on lg.com — onto sksappliances.com. The bundle below is the one Transcend
 * provisioned for the sksappliances.com domain.
 */
const AIRGAP_SRC =
    "https://transcend-cdn.com/cm/225b937d-9be3-4678-ab53-ffbcc1380dc3/airgap.js";

/**
 * In *report-only* mode: `data-report-only="on"` makes airgap observe
 * and report traffic without blocking anything, and `data-prompt="0"` keeps the
 * consent banner hidden.
 */
const AIRGAP_REPORT_ONLY = false;

/**
 * Inject airgap.js into the document head.
 *
 * Called from `<TranscendAirgapLoader>`'s `useLayoutEffect` (see
 * `client/components/TranscendAirgapLoader.tsx`) — NOT from the top of the
 * client entry, and NOT from a plain `useEffect`:
 *
 *   - airgap.js is a *consent gate*. It works by wrapping `document.createElement`,
 *     `fetch`, `XMLHttpRequest`, `sendBeacon`, etc. the moment it executes, so it
 *     can intercept and gate other tracking scripts. That only works if it lands
 *     BEFORE those scripts. The `data-cfasync="false"` attribute is literally
 *     "do not async/defer me — load me first."
 *   - It must run AFTER hydration, though. This mutates `<head>` (inserts a
 *     `<script>`); doing so before `hydrateRoot` finishes shifts the positions
 *     of the server-rendered `<head>` nodes and makes React hydrate onto the
 *     wrong node → hydration mismatch. An effect fires only after the commit.
 *   - GTM and OneTrust inject themselves from a passive `useEffect`. React
 *     flushes all layout effects before any passive effect, so injecting from a
 *     `useLayoutEffect` still lands airgap's `<script>` ahead of GTM's — it wins
 *     the ordering that lets it gate GTM once report-only is removed.
 *
 * Why this is still CSP-safe under our `strict-dynamic` policy: the airgap
 * `<script>` carries no nonce or SRI hash, but it is created by an
 * already-trusted (SRI-hashed) bundle script. Under `strict-dynamic`, trust
 * propagates to scripts that a trusted script injects, so airgap.js executes and
 * `transcend-cdn.com` needs no `script-src` host entry. (airgap's *network*
 * calls still need `connect-src` entries — see `_csp_transcend` in settings.py.)
 */
export const injectTranscendAirgap = (): void => {
    // Client-only: the server-render path (renderer.tsx) never calls this, but
    // guard anyway so the module is safe to import anywhere.
    if (typeof document === "undefined") {
        return;
    }
    // Idempotency guard — a duplicate import or re-entrant call must not inject
    // airgap twice. Match the exact bundle URL (not just the /cm/ path prefix)
    // so an unrelated Transcend module under /cm/ can't suppress this one.
    if (document.querySelector(`script[src="${AIRGAP_SRC}"]`)) {
        return;
    }
    const script = document.createElement("script");
    script.src = AIRGAP_SRC;
    script.setAttribute("data-cfasync", "false");
    script.setAttribute("data-css", transcendCssUrl);
    if (AIRGAP_REPORT_ONLY) {
        script.setAttribute("data-report-only", "on");
        script.setAttribute("data-prompt", "0");
    }
    // Insert ahead of the first existing script so airgap loads as early as
    // possible (mirrors the GoogleTagManagerBlock insertion idiom).
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }
};
